<template>
  <section>
    <div class="relative grid gap-8 pt-12 lg:grid-cols-3">
      <div
        v-if="
          !loading &&
          !isLoading &&
          currentMonthSchedules.length === 0 &&
          endedEvents.length === 0
        "
        class="text-center text-neutral-400 lg:col-span-2"
      >
        There are currently no scheduled events for this month
        {{ blok.headline ? `at ${blok.headline}` : '' }}.
      </div>
      <div class="space-y-6 lg:col-span-2">
        <div
          v-if="endedEvents.length > 0"
          :class="[
            'relative h-24 w-full',
            endedEvents.length >= 3 ? 'mb-3 sm:mb-6' : '',
            endedEvents.length === 2 ? 'mb-2 sm:mb-4' : '',
            endedEvents.length === 1 ? 'mb-1 sm:mb-2' : '',
          ]"
        >
          <UCard
            v-for="(event, index) in endedEvents.slice(0, 3)"
            :key="index"
            class="absolute w-full"
            :style="{ top: `${index * 12}px`, zIndex: `${5 - index}` }"
            :ui="{
              root: 'bg-neutral-900/60 backdrop-blur-lg',
            }"
          >
            <div class="text-center text-lg font-extrabold text-yellow-500/60">
              {{ endedEvents.length }}
              {{ endedEvents.length > 1 ? 'Events' : 'Event' }} Ended
            </div>
          </UCard>
        </div>
        <LazyMarketingCardEvent
          v-for="(scheduleItem, index) in currentMonthSchedules"
          v-if="!loading && !isLoading && currentMonthSchedules.length > 0"
          :key="index"
          :note="scheduleItem.note"
          :event="scheduleItem"
          :hovered-date="hoveredDate"
        />
        <LazyMarketingCardEventLoading
          v-for="i in 3"
          v-if="loading || isLoading"
          :key="i"
        />
      </div>
      <UCard
        class="order-first h-fit lg:sticky lg:top-56 lg:order-last"
        :ui="{
          root: 'bg-neutral-900/60 backdrop-blur-lg overflow-hidden',
          body: 'p-0 sm:p-0',
        }"
      >
        <template #header>
          <div class="flex items-center justify-between">
            <ClientOnly>
              <UButton
                icon="i-lucide-chevron-left"
                variant="subtle"
                :disabled="!hasPrevMonth"
                @click="prevMonth"
              />
            </ClientOnly>
            <div class="flex items-center justify-center font-bold">
              <span>{{ currentMonthYear }}</span>
              <span class="text-sm font-medium text-yellow-400"
                ><UTooltip
                  :text="`${currentMonthSchedules.length} Remaining`"
                  :ui="{ text: 'font-bold' }"
                >
                  <UButton size="md" variant="link"
                    >({{ eventCount.length }})</UButton
                  >
                </UTooltip>
              </span>
            </div>
            <ClientOnly>
              <UButton
                icon="i-lucide-chevron-right"
                variant="subtle"
                :disabled="!hasNextMonth"
                @click="nextMonth"
              />
            </ClientOnly>
          </div>
        </template>
        <div class="hidden lg:block">
          <!-- Month Calendar View -->
          <div class="grid grid-cols-7 text-center">
            <!-- Header: Days of the week starting from Monday -->
            <div
              v-for="day in ['M', 'T', 'W', 'T', 'F', 'S', 'S']"
              :key="day"
              class="flex aspect-[1/1] h-full w-full items-center justify-center bg-black/50 text-sm font-bold text-neutral-400 ring ring-neutral-800"
            >
              {{ day }}
            </div>
          </div>

          <!-- Calendar Days -->
          <div class="grid grid-cols-7 text-center">
            <!-- Days from the previous month -->
            <div
              v-for="day in previousMonthDays"
              :key="'prev-' + day"
              class="relative flex aspect-[1/1] h-full w-full items-center justify-center font-medium text-neutral-400 ring ring-neutral-800 hover:bg-neutral-900"
            >
              {{ day }}

              <!-- Display dots for each event under the date for the previous month -->
              <div
                v-if="hasEvents(day, true)"
                class="absolute inset-0 mx-auto mt-8 flex items-center justify-center space-x-1"
              >
                <div
                  v-for="event in getEventsForDay(day, true).slice(0, 3)"
                  :key="event.start"
                  class="h-1.5 w-1.5 rounded-full bg-yellow-400"
                />
              </div>
            </div>

            <!-- Days of the current month -->
            <div
              v-for="day in daysInMonth"
              :key="'day-' + day"
              :class="{
                'font-extrabold text-yellow-400': isToday(day),
                'font-semibold': !isToday(day),
              }"
              class="relative flex aspect-[1/1] h-full w-full flex-col justify-center text-white ring ring-neutral-800 hover:bg-neutral-900"
              @mouseenter="handleDateMouseEnter(day)"
              @mouseleave="handleDateMouseLeave"
            >
              <div
                :class="{
                  'mx-auto flex aspect-[1/1] h-5/8 w-5/8 items-center justify-center rounded-full bg-neutral-800 text-center':
                    isToday(day),
                }"
              >
                {{ day }}
              </div>

              <!-- Display dots for each event under the date -->
              <div
                v-if="hasEvents(day)"
                class="absolute inset-0 mx-auto mt-8 flex items-center justify-center space-x-1"
              >
                <div
                  v-for="event in getEventsForDay(day).slice(0, 3)"
                  :key="event.start"
                  class="h-1.5 w-1.5 rounded-full bg-yellow-400"
                />
              </div>
            </div>

            <!-- Days from the next month -->
            <div
              v-for="day in nextMonthDays"
              :key="'next-' + day"
              class="relative flex aspect-[1/1] h-full w-full items-center justify-center font-medium text-neutral-400 ring ring-neutral-800 hover:bg-neutral-900"
            >
              {{ day }}

              <!-- Display dots for each event under the date for the next month -->
              <div
                v-if="hasEvents(day, false, true)"
                class="absolute inset-0 mx-auto mt-8 flex items-center justify-center space-x-1"
              >
                <div
                  v-for="event in getEventsForDay(day, false, true).slice(0, 3)"
                  :key="event.start"
                  class="h-1.5 w-1.5 rounded-full bg-yellow-400"
                />
              </div>
            </div>
          </div>
        </div>
        <template v-if="monthOffset !== 0" #footer>
          <UButton
            class="w-full justify-center text-center"
            @click="goToCurrentMonth"
            >Go To This Month</UButton
          >
        </template>
      </UCard>
    </div>
  </section>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import type {
  EventStoryblok,
  ScheduleStoryblok,
  LocationStoryblok,
  ActivityStoryblok,
} from '~/types/component-types-sb';

interface Props {
  blok?: EventStoryblok;
  uuid?: string;
  type?: 'location' | 'activity' | '';
}

interface ScheduleItem extends ScheduleStoryblok {
  note?: string;
  event: EventStoryblok;
  start: string;
  end: string;
}

const props = withDefaults(defineProps<Props>(), {
  blok: () => ({}) as EventStoryblok,
  uuid: '',
  type: '',
});

const emit = defineEmits<{
  (e: 'events-false'): void;
  (e: 'events-count', count: number): void;
}>();

const { fetchEvents, isLoading } = useStoryblokClient();

const events = ref<EventStoryblok[]>([]);
const loading = ref(true);

// Load events and set them in the reactive `events` array
const loadEvents = async () => {
  loading.value = true;
  try {
    const [stories] = await Promise.all([fetchEvents()]);
    events.value = stories;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

loadEvents();

const filteredEvents = computed(() => {
  if (props.type === 'location') {
    return events.value.filter((event) =>
      event.content?.locations?.some(
        (location: LocationStoryblok) => location.uuid === props.uuid
      )
    );
  } else if (props.type === 'activity') {
    return events.value.filter((event) =>
      event.content?.activities?.some(
        (activity: ActivityStoryblok) => activity.uuid === props.uuid
      )
    );
  } else {
    return events.value;
  }
});

// Step 1: Extract all schedules from all events (after events are loaded)
const schedules = computed<ScheduleItem[]>(() => {
  // Calculate the start of the current month
  const currentMonthStart = dayjs().startOf('month');

  if (!filteredEvents.value.length) return [];

  return filteredEvents.value.flatMap((event) => {
    return (
      event.content?.schedule
        ?.filter((scheduleItem: ScheduleStoryblok) => {
          const scheduleStartDate = dayjs(scheduleItem.start);
          // Include schedule items that start on or after the current month
          return (
            scheduleStartDate.isValid() &&
            scheduleStartDate.isSameOrAfter(currentMonthStart)
          );
        })
        .map((scheduleItem: ScheduleStoryblok) => ({
          ...scheduleItem,
          note: scheduleItem.notes,
          event: event,
        })) || []
    );
  });
});

watch(schedules, (newVal) => {
  if (newVal) {
    emit('events-count', schedules.value.length);
  }
  if (newVal && schedules.value.length === 0) {
    emit('events-false');
  }
});

// Step 2: Sort schedules by start date
const sortedSchedules = computed(() => {
  return [...schedules.value].sort((a, b) => {
    if (!a.start || !b.start) return 0;
    return new Date(a.start).getTime() - new Date(b.start).getTime();
  });
});

// Step 3: Pagination logic for months
const currentDate = ref(new Date());
const monthOffset = ref(0);

// Step 4: Compute ended events (events where the end date is in the past)
const endedEvents = computed(() => {
  const now = new Date();
  return sortedSchedules.value.filter((scheduleItem) => {
    if (!scheduleItem.start) return false;
    const startDate = new Date(scheduleItem.start);
    const endDate = scheduleItem.end ? new Date(scheduleItem.end) : startDate;
    return (
      endDate < now &&
      startDate >= currentMonthStart.value &&
      endDate <= currentMonthEnd.value
    );
  });
});

// Function to get the start and end of the month based on offset
const getStartOfMonth = (date: Date) => {
  const d = new Date(date);
  d.setDate(1); // Set to the first day of the month
  d.setMonth(d.getMonth() + monthOffset.value); // Adjust for the month offset
  d.setHours(0, 0, 0, 0); // Set to start of the day
  return d;
};

const getEndOfMonth = (startOfMonth: Date) => {
  const d = new Date(startOfMonth);
  d.setMonth(d.getMonth() + 1); // Move to next month
  d.setDate(0); // Set to last day of current month
  d.setHours(23, 59, 59, 999); // Set to end of the day
  return d;
};

const currentMonthStart = computed(() => {
  const startOfMonth = getStartOfMonth(currentDate.value);
  return startOfMonth;
});

const currentMonthEnd = computed(() => {
  const end = getEndOfMonth(currentMonthStart.value);
  return end;
});

const currentMonthSchedules = computed(() => {
  const now = new Date();
  const isCurrentMonth = monthOffset.value === 0;

  return sortedSchedules.value.filter((scheduleItem) => {
    if (!scheduleItem.start) return false;
    const startDate = new Date(scheduleItem.start);
    const endDate = scheduleItem.end ? new Date(scheduleItem.end) : startDate;

    // Only apply the "not ended" check when we're viewing the current month
    const notEndedCheck = isCurrentMonth ? endDate >= now : true;

    return (
      notEndedCheck &&
      ((startDate >= currentMonthStart.value &&
        startDate <= currentMonthEnd.value) ||
        (endDate >= currentMonthStart.value &&
          endDate <= currentMonthEnd.value) ||
        (startDate < currentMonthStart.value &&
          endDate > currentMonthEnd.value))
    );
  });
});

// Computed property to display current month and year
const currentMonthYear = computed(() => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
  };
  return currentMonthStart.value.toLocaleDateString(undefined, options);
});

// Pagination functions
const prevMonth = () => {
  monthOffset.value--;
  // Scroll to the top of the section
  window.scrollTo({ top: 416, behavior: 'smooth' });
};

const nextMonth = () => {
  monthOffset.value++;
  // Scroll to the top of the section
  window.scrollTo({ top: 416, behavior: 'smooth' });
};

// Get the first day of the current real month (i.e., current date without offset)
const currentRealMonthStart = computed(() => {
  const d = new Date();
  d.setDate(1); // Set to the first day of the current real month
  return d;
});

// Use useState for shared state between server and client
const isClient = useState('is-client', () => false);

// Update isClient state when component mounts
onMounted(() => {
  isClient.value = true;
});

// Disable buttons when there are no more months to show
const hasPrevMonth = computed(() => {
  if (!isClient.value) return false; // Return false during SSR
  if (currentMonthStart.value <= currentRealMonthStart.value) return false;
  return sortedSchedules.value.some(
    (scheduleItem) =>
      scheduleItem.start &&
      new Date(scheduleItem.start) < currentMonthStart.value
  );
});

const hasNextMonth = computed(() => {
  if (!isClient.value) return false; // Return false during SSR
  return sortedSchedules.value.some(
    (scheduleItem) =>
      scheduleItem.start && new Date(scheduleItem.start) > currentMonthEnd.value
  );
});

// Method to go back to the current month
const goToCurrentMonth = () => {
  monthOffset.value = 0;
  // Scroll to the top of the section
  window.scrollTo({ top: 416, behavior: 'smooth' });
};

// Function to get the total number of days in the current month
const daysInMonth = computed(() => {
  const days = new Date(
    currentMonthStart.value.getFullYear(),
    currentMonthStart.value.getMonth() + 1,
    0
  ).getDate();
  return [...Array(days)].map((_, index) => index + 1);
});

// Adjust the first day of the month so that Monday is treated as the first day of the week
const adjustedFirstDayOfMonth = computed(() => {
  const firstDay = currentMonthStart.value.getDay(); // Get the first day (0 = Sunday, 1 = Monday, etc.)
  return firstDay === 0 ? 6 : firstDay - 1; // If it's Sunday (0), set to 6; otherwise subtract 1 to shift days
});

// Calculate how many empty slots are needed after the last day of the month
const emptySlotsAfterLastDay = computed(() => {
  const lastDay = currentMonthEnd.value.getDay(); // Get the day of the week for the last day of the current month
  const daysRemaining = (7 - lastDay) % 7; // Calculate how many days are left in the week after the last day
  return daysRemaining;
});

// Calculate the days for the previous month to fill the empty slots at the beginning
const previousMonthDays = computed(() => {
  const startOfPreviousMonth = new Date(
    currentMonthStart.value.getFullYear(),
    currentMonthStart.value.getMonth(),
    0
  ); // Get last day of the previous month
  const lastDateOfPreviousMonth = startOfPreviousMonth.getDate(); // Get the last date of the previous month
  return [...Array(adjustedFirstDayOfMonth.value)]
    .map((_, index) => lastDateOfPreviousMonth - index)
    .reverse();
});

// Calculate the days for the next month to fill the empty slots at the end
const nextMonthDays = computed(() => {
  return [...Array(emptySlotsAfterLastDay.value)].map((_, index) => index + 1);
});

// Function to check if a given day is the current day
const isToday = (day: number) => {
  const today = new Date();
  const currentMonth = currentMonthStart.value.getMonth();
  const currentYear = currentMonthStart.value.getFullYear();

  return (
    today.getDate() === day &&
    today.getMonth() === currentMonth &&
    today.getFullYear() === currentYear
  );
};

// Check if there are events on a given day, considering both single-day and multi-day events
const hasEvents = (
  day: number,
  isPreviousMonth = false,
  isNextMonth = false
) => {
  const currentDate = new Date(currentMonthStart.value);

  if (isPreviousMonth) {
    currentDate.setMonth(currentDate.getMonth() - 1);
  } else if (isNextMonth) {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  currentDate.setDate(day);

  return schedules.value.some((scheduleItem) => {
    if (!scheduleItem.start) return false;
    const startDate = new Date(scheduleItem.start);
    const endDate = scheduleItem.end ? new Date(scheduleItem.end) : startDate; // Handle single-day events
    // Include single-day and multi-day events
    return (
      currentDate.toDateString() === startDate.toDateString() ||
      (currentDate >= startDate && currentDate <= endDate)
    );
  });
};

const getEventsForDay = (
  day: number,
  isPreviousMonth = false,
  isNextMonth = false
) => {
  const currentDate = new Date(currentMonthStart.value);

  if (isPreviousMonth) {
    currentDate.setMonth(currentDate.getMonth() - 1);
  } else if (isNextMonth) {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  currentDate.setDate(day);

  return schedules.value.filter((scheduleItem) => {
    if (!scheduleItem.start) return false;
    const startDate = new Date(scheduleItem.start);
    const endDate = scheduleItem.end ? new Date(scheduleItem.end) : startDate; // Handle single-day events
    // Include single-day and multi-day events
    return (
      currentDate.toDateString() === startDate.toDateString() ||
      (currentDate >= startDate && currentDate <= endDate)
    );
  });
};

// New reactive variable to store the hovered date
const hoveredDate = ref<Date | null>(null);

// Handler for mouseenter event
const handleDateMouseEnter = (day: number) => {
  const fullDate = new Date(
    currentMonthStart.value.getFullYear(),
    currentMonthStart.value.getMonth(),
    day
  );
  hoveredDate.value = fullDate;
};

// Handler for mouseleave event
const handleDateMouseLeave = () => {
  hoveredDate.value = null;
};

// Month Event Count
const eventCount = computed(() => {
  return sortedSchedules.value.filter((scheduleItem) => {
    if (!scheduleItem.start) return false;
    const startDate = new Date(scheduleItem.start);
    const endDate = scheduleItem.end ? new Date(scheduleItem.end) : startDate;
    return (
      (startDate >= currentMonthStart.value &&
        startDate <= currentMonthEnd.value) ||
      (endDate >= currentMonthStart.value &&
        endDate <= currentMonthEnd.value) ||
      (startDate < currentMonthStart.value && endDate > currentMonthEnd.value)
    );
  });
});

// Add a mounted hook to handle client-side initialization
onMounted(() => {
  // Force a re-render after hydration to ensure correct button states
  nextTick(() => {
    // Trigger a re-render by updating a dummy value
    const temp = monthOffset.value;
    monthOffset.value = temp + 0;
  });
});
</script>
